import { useAnalytics } from "@/features/analytics/context/AnalyticsProvider";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { UsageAlert } from "@/features/usage/components/UsageAlert";
import { useDailyPrompts, useWorkspaceStorage } from "@/features/usage/hooks/useLimit";
import { cn } from "@/utils/cn";
import { ArrowUp, Grid3x3, Paperclip, Square, X } from "lucide-react";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useChatSession } from "../context/ChatSessionProvider";
import { useDragDrop } from "../hooks/useDragDrop";
import { SUPPORTED_EXTENSIONS, useFileAttachments } from "../hooks/useFileAttachments";
import { useIsChatHome } from "../hooks/useIsChatHome";
import { saveDraftPrompt, takeDraftPrompt } from "../store/draftPrompt";
import { DragDropOverlay } from "./DragDropOverlay";
import { EditableDiv, EditableDivChangeHandler, EditableDivHandle, EditableDivKeyDownHandler, editorIsReady } from "./EditableDiv";
import { FileAttachmentChip } from "./FileAttachmentChip";

const LINE_HEIGHT = 28; // matches leading-7 on the editor
const REST_LINES = 1;
const FOCUSED_LINES = 2;
const DEFAULT_EDITOR_HEIGHT = LINE_HEIGHT * REST_LINES;
const CHAT_PROMPTS_ALERT_ID = "usage-alert-chat-prompts";
const CHAT_STORAGE_ALERT_ID = "usage-alert-chat-storage";

/** Sample prompts shown above the composer on the home state, one at a time
 * on a rolling ticker; clicking the visible one drops it into the field. */
const SAMPLE_PROMPTS = [
  "What is the IC50 of MRTX1133 against KRAS G12D?",
  "Which FDA labels warn about QTc prolongation?",
  "Compare sotorasib and adagrasib in NSCLC trials",
  "Which bioRxiv preprints on GLP-1 agonists were later published?",
  "Summarize the eligibility criteria of NCT04280705",
];
const SAMPLE_DWELL_MS = 3600;
const SAMPLE_ROLL_MS = 420;

/**
 * One line of real estate: the current prompt rolls up and out while the
 * next rolls in from below. Pauses while hovered so it can be clicked;
 * swaps without motion under prefers-reduced-motion.
 */
const SampleTicker = ({
  prompts,
  large,
  onPick,
}: {
  prompts: readonly string[];
  large: boolean;
  onPick: (text: string) => void;
}) => {
  const [index, setIndex] = useState(0);
  const [leaving, setLeaving] = useState<number | null>(null);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const tick = window.setInterval(() => {
      setIndex((i) => {
        if (!reduced) setLeaving(i);
        return (i + 1) % prompts.length;
      });
    }, SAMPLE_DWELL_MS);
    return () => window.clearInterval(tick);
  }, [paused, prompts.length]);

  useEffect(() => {
    if (leaving === null) return;
    const t = window.setTimeout(() => setLeaving(null), SAMPLE_ROLL_MS);
    return () => window.clearTimeout(t);
  }, [leaving]);

  const current = prompts[index % prompts.length];
  return (
    <div
      className={`chat-samples${large ? " chat-samples--large" : ""}`}
      aria-label="Sample questions"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <span>Try</span>
      <div className="chat-samples-window" aria-live="polite">
        {leaving !== null && (
          <span key={`out-${leaving}`} className="chat-sample chat-sample--out" aria-hidden>
            {prompts[leaving % prompts.length]}
          </span>
        )}
        <button
          key={`in-${index}`}
          type="button"
          className={`chat-sample${leaving !== null ? " chat-sample--in" : ""}`}
          onClick={() => onPick(current)}
        >
          {current}
        </button>
      </div>
    </div>
  );
};

export interface PromptInputHandle {
  /** Returns false while the editor is not ready yet. */
  setText: (text: string) => boolean;
  focus: () => void;
  /** Sends the current text, as the Send button does. */
  submit: () => void;
  /** The text that Send would send, without files. Empty when the user typed nothing. */
  unsentText: () => string;
  /** Empties the box and its attached items, after their text went out in another message. */
  clear: () => void;
}

export interface PromptContextChip {
  key: string;
  label: string;
}

/** Items attached to the next message: chips above the input, and text added on send. */
export interface PromptContext {
  chips: PromptContextChip[];
  text: string;
  onRemove: (key: string) => void;
  onSent: () => void;
}

interface PromptInputProps {
  /** Narrow layout for side panels: no sample ticker or footer. */
  compact?: boolean;
  context?: PromptContext;
  placeholder?: string;
  /** Prompts the home-state ticker rolls through. */
  samplePrompts?: readonly string[];
  /** Shows the sample prompts in the larger text of the page around them. */
  largeSamples?: boolean;
  /** Controls shown before Attach, such as the Dataset builder mode. */
  actions?: ReactNode;
  /** Takes the message instead of the chat session, such as a Fast dataset build. */
  onSend?: (text: string) => void;
}

export const PromptInput = forwardRef<PromptInputHandle, PromptInputProps>(({ compact = false, context, placeholder = "Ask a question...", samplePrompts = SAMPLE_PROMPTS, largeSamples = false, actions, onSend }, ref) => {
  const contextText = context && context.chips.length > 0 ? context.text : "";
  const { sendMessage, stopStreaming, isStreaming, isLoading, sessionId } =
    useChatSession();
  const { trackEvent } = useAnalytics();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const editorRef = useRef<EditableDivHandle>(null);
  const [hasText, setHasText] = useState(false);
  const [contentHeight, setContentHeight] = useState(DEFAULT_EDITOR_HEIGHT);
  const [isFocused, setIsFocused] = useState(false);
  const isHome = useIsChatHome() && !compact;
  // Rests at one line; opens to two on focus; grows with content past that.
  const baseHeight = LINE_HEIGHT * (isFocused ? FOCUSED_LINES : REST_LINES);
  const editorHeight = hasText ? Math.max(contentHeight, baseHeight) : baseHeight;

  const {
    fileInputRef,
    selectedFiles,
    pendingFileNames,
    isProcessingFiles,
    getSelectedFiles,
    processFiles,
    handleFileInput,
    removeFileByName,
    clearSelectedFiles,
  } = useFileAttachments();
  const {
    isAtLimit: promptsAtLimit,
    isApproachingLimit: promptsApproaching,
    limit: promptsLimit,
  } = useDailyPrompts();
  const {
    isAtLimit: storageAtLimit,
    isApproachingLimit: storageApproaching,
    limit: storageLimit,
  } = useWorkspaceStorage();

  const { isDragging } = useDragDrop({
    onFilesDropped: (files) => processFiles(files),
    enabled: !isStreaming && !isLoading && !isProcessingFiles && !storageAtLimit && !promptsAtLimit,
  });

  const canSubmit =
    (hasText || selectedFiles.length > 0 || contextText !== "") &&
    !isProcessingFiles &&
    !promptsAtLimit;

  const submit = useCallback(() => {
    const text = editorRef.current?.text ?? "";
    const trimmed = text.trim();
    if (!trimmed && selectedFiles.length === 0 && !contextText) return;
    if (isStreaming || isLoading) return;
    if (promptsAtLimit) return;

    // Visitors can compose on the home page; sending needs an account. Keep
    // the draft so it is waiting for them when they come back from /login.
    if (!user) {
      saveDraftPrompt(trimmed);
      const next = `${location.pathname}${location.search}`;
      navigate(`/login?next=${encodeURIComponent(next)}`);
      return;
    }

    const files = getSelectedFiles();
    if (!sessionId) {
      trackEvent("new_session", {
        source: "splash_screen",
        has_initial_prompt: true,
      });
    }
    trackEvent("submit_prompt", {
      source: "chat_view",
      prompt_length: trimmed.length,
      attachment_count: files.length,
    });

    let messageText = [trimmed, contextText].filter(Boolean).join("\n\n");
    if (files.length > 0) {
      const refs = files
        .map((f) => {
          const path = f.path ?? `/clipboard/chat_uploads/${f.short_id}`;
          if (f.kind === "folder") {
            return `<clipboard_folder name="${f.filename}" path="${path}"></clipboard_folder>`;
          }
          return `<uploaded_document filename="${f.filename}" path="${path}" size="${f.size}"></uploaded_document>`;
        })
        .join("\n");
      messageText = messageText ? `${messageText}\n${refs}` : refs;
    }

    if (onSend) onSend(messageText);
    else sendMessage(messageText);
    editorRef.current?.clear();
    clearSelectedFiles();
    if (contextText) context?.onSent();
  }, [
    contextText,
    context,
    selectedFiles.length,
    isStreaming,
    isLoading,
    getSelectedFiles,
    sendMessage,
    onSend,
    clearSelectedFiles,
    sessionId,
    trackEvent,
    promptsAtLimit,
    user,
    navigate,
    location.pathname,
    location.search,
  ]);

  // Restore a draft written before signing in.
  useEffect(() => {
    if (!user || sessionId) return;
    const draft = takeDraftPrompt();
    if (!draft || editorRef.current?.text) return;
    editorRef.current?.setText(draft);
    setHasText(true);
    editorRef.current?.focus();
  }, [user, sessionId]);

  const handleEnterKey: EditableDivKeyDownHandler = useCallback(
    ({ event }) => {
      event.preventDefault();
      submit();
    },
    [submit],
  );

  const handleChange: EditableDivChangeHandler = useCallback((text, editor) => {
    setHasText(!!text.trim().length);

    if (!editorIsReady(editor)) return;
    const editorContent = editor?.view.dom.children[0] as HTMLParagraphElement;
    const editorContentHeight = editorContent?.scrollHeight ?? DEFAULT_EDITOR_HEIGHT;

    setContentHeight(text.length ? editorContentHeight : DEFAULT_EDITOR_HEIGHT);
  }, []);

  const submitRef = useRef(submit);
  useEffect(() => {
    submitRef.current = submit;
  }, [submit]);

  const contextRef = useRef({ text: contextText, onSent: context?.onSent });
  useEffect(() => {
    contextRef.current = { text: contextText, onSent: context?.onSent };
  }, [contextText, context?.onSent]);

  useImperativeHandle(ref, () => ({
    setText: (text: string) => {
      if (!editorIsReady(editorRef.current?.editor)) return false;
      editorRef.current?.setText(text);
      return true;
    },
    focus: () => {
      editorRef.current?.focus();
    },
    submit: () => submitRef.current(),
    unsentText: () => {
      const typed = editorRef.current?.text.trim() ?? "";
      return typed ? [typed, contextRef.current.text].filter(Boolean).join("\n\n") : "";
    },
    clear: () => {
      editorRef.current?.clear();
      if (contextRef.current.text) contextRef.current.onSent?.();
    },
  }), []);

  const pickSample = useCallback((text: string) => {
    editorRef.current?.setText(text);
    setHasText(true);
    editorRef.current?.focus();
  }, []);

  const handleFormSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      submit();
    },
    [submit],
  );

  return (
    <>
      {isDragging && <DragDropOverlay />}

      <div className="prompt-box-container shrink-0 bg-transparent">
        <div
          className={cn(
            "prompt-box-items relative w-full space-y-2",
            compact ? "px-3 pb-3" : "max-w-[58rem] px-6 -translate-y-3",
            isHome ? "chat-home-col" : "mx-auto",
          )}
        >
          {isHome && samplePrompts.length > 0 && (
            <SampleTicker prompts={samplePrompts} large={largeSamples} onPick={pickSample} />
          )}
          <UsageAlert
            id={CHAT_PROMPTS_ALERT_ID}
            name="dailyPrompts"
            isAtLimit={promptsAtLimit}
            isApproachingLimit={promptsApproaching}
            limit={promptsLimit}
          />
          <UsageAlert
            id={CHAT_STORAGE_ALERT_ID}
            name="workspaceStorage"
            isAtLimit={storageAtLimit}
            isApproachingLimit={storageApproaching}
            limit={storageLimit}
          />
          <form
            aria-label="Send a message"
            aria-describedby={
              [
                promptsAtLimit || promptsApproaching ? CHAT_PROMPTS_ALERT_ID : null,
                storageAtLimit || storageApproaching ? CHAT_STORAGE_ALERT_ID : null,
              ]
                .filter(Boolean)
                .join(" ") || undefined
            }
            onSubmit={handleFormSubmit}
            onFocus={() => setIsFocused(true)}
            onBlur={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setIsFocused(false);
            }}
            className={cn(
              "prompt-box prompt-box--editorial",
              isFocused && "is-focused",
              "flex flex-col bg-transparent",
            )}
          >
            <label htmlFor="prompt-input" id="prompt-input-label" className="sr-only">
              Message
            </label>
            <p id="prompt-input-hint" className="sr-only">
              Enter to send, Shift+Enter for new line.
            </p>
            {context && context.chips.length > 0 && (
              <ul className="flex flex-wrap gap-1.5 pt-1 pb-2.5" aria-label="Attached to the message">
                {context.chips.map((chip) => (
                  <li
                    key={chip.key}
                    className="flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 py-0.5 pl-2 pr-1 text-xs text-amber-900"
                  >
                    <Grid3x3 className="h-3 w-3 shrink-0 text-amber-700" aria-hidden />
                    <span className="max-w-[200px] truncate" title={chip.label}>
                      {chip.label}
                    </span>
                    <button
                      type="button"
                      onClick={() => context.onRemove(chip.key)}
                      aria-label={`Remove ${chip.label}`}
                      title="Remove"
                      className="rounded-full p-0.5 text-amber-700 hover:bg-amber-100 hover:text-red-700"
                    >
                      <X className="h-3 w-3" aria-hidden />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {/* File chips (inside the unified box) */}
            {(selectedFiles.length > 0 || pendingFileNames.length > 0) && (
              <div className="flex flex-wrap gap-1.5 pt-1 pb-2.5">
                {selectedFiles.map((f) => (
                  <FileAttachmentChip
                    key={f.filename}
                    filename={f.filename}
                    isUploaded
                    isFolder={f.kind === "folder"}
                    onRemove={() => removeFileByName(f.filename)}
                  />
                ))}
                {pendingFileNames.map((name) => (
                  <FileAttachmentChip key={name} filename={name} isPending />
                ))}
              </div>
            )}

            {/* Input row */}
            <div
              className={cn(
                "flex min-w-0",
                compact ? "flex-col gap-2" : "flex-wrap items-end gap-x-4 gap-y-2",
              )}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept={SUPPORTED_EXTENSIONS.join(",")}
                multiple
                onChange={handleFileInput}
                className="hidden"
              />

              {/* TipTap Editor */}
              <EditableDiv
                ref={editorRef}
                id="prompt-input"
                ariaLabelledBy="prompt-input-label prompt-input-hint"
                height={editorHeight}
                disabled={isLoading || isStreaming || isProcessingFiles || promptsAtLimit}
                placeholder={promptsAtLimit ? "Daily query limit reached" : placeholder}
                className={cn(
                  "min-w-0 overflow-hidden",
                  compact ? "w-full" : "flex-1 basis-[min(100%,20rem)]",
                )}
                editorClassName="prompt-editorial text-base leading-7 text-zinc-900 !px-0 !py-0 !min-h-0 transition-[height] duration-200 ease-linear"
                focusOnMount={false}
                handleEnterKey={handleEnterKey}
                handleChange={handleChange}
              />

              {/* attach · send / stop — quiet serif actions */}
              <div
                className={cn(
                  "font-ui flex items-center text-sm leading-none",
                  compact
                    ? "flex-wrap justify-end gap-x-4 gap-y-2"
                    : "ml-auto max-w-full flex-wrap justify-end gap-x-5 gap-y-2 pb-[7px]",
                )}
              >
                {actions}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isProcessingFiles || storageAtLimit || promptsAtLimit}
                  aria-label="Attach file"
                  aria-describedby={storageAtLimit ? CHAT_STORAGE_ALERT_ID : undefined}
                  title={storageAtLimit ? "Workspace storage is full" : "Attach file"}
                  className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-zinc-700 disabled:opacity-40 transition-colors"
                >
                  <Paperclip className="h-3.5 w-3.5" aria-hidden />
                  Attach
                </button>
                {isStreaming ? (
                  <button
                    type="button"
                    onClick={stopStreaming}
                    aria-label="Stop generating"
                    title="Stop generating"
                    className="inline-flex items-center gap-1.5 text-zinc-900 hover:text-zinc-700 transition-colors"
                  >
                    <Square className="h-3 w-3" aria-hidden />
                    Stop
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={isLoading || !canSubmit}
                    aria-label="Send message"
                    title="Send message"
                    className={cn(
                      "inline-flex items-center gap-1.5 transition-colors",
                      canSubmit ? "text-zinc-900 hover:text-zinc-700" : "text-zinc-300",
                    )}
                  >
                    Send
                    <ArrowUp className="h-3.5 w-3.5" aria-hidden />
                  </button>
                )}
              </div>
            </div>
          </form>
          {!compact && (
            <p className="mt-2 flex justify-between px-1 text-sm leading-5 text-zinc-400">
              <span>Enter to send · Shift+Enter for a new line</span>
              <a href="mailto:support@gxl.ai" className="font-ui hover:text-zinc-700">Questions or feedback?</a>
            </p>
          )}
        </div>
      </div>
    </>
  );
});

PromptInput.displayName = "PromptInput";
